import { getStore } from '../persistence/store';
import { appendAppLog } from '../platform/appLog';
import { autoUpload, meldeWecker, wiederholeBisRuhe } from '../platform/anstoss';
import { appModus } from '../platform/appZustand';
import { zaehleRueckstand } from './rueckstand';
import { APP_VERSION_MIT_BUILD } from '../platform/appVersion';
import { systemJetzt } from '../platform/betriebssystem';
import { Signing } from '../platform/signing';
import { beiEinstellungsAenderung } from '../platform/einstellungenGeaendert';
import type { Gemischt } from '../platform/einstellungen';
import { relaySender, uebertrageAnhaenge } from './anhaenge';
import {
  gleicheEinstellungenAb,
  markiereMeldungFaellig,
  meldungFaellig,
  setzeMeldeWecker,
  type AbgleichDeps,
} from './geraeteEinstellungen';
import { ladeBildschirmfotoHoch } from './bildschirmfoto';
import { ladeProtokolleHoch } from './protokollArchiv';
import { createSync } from './sync';
import { istNetzausfall } from './verbindung';
import { baueClient, istEingerichtet, ladeZugang } from './zugang';

/**
 * Der 10-Sekunden-Takt des **Relay**-Modus.
 *
 * ⚠️ **Erst die Einträge, dann die Dateien.** Der `ExamLog`-Eintrag kündigt an, wie viele
 * Aufnahmen einer Art an einer Aufgabe hängen (`value` trägt seit dem 2026-09-14 die
 * **Anzahl**, s. `domain/medienzahl.ts`). Kämen die Anhänge zuerst, sähe der Server eine Weile
 * mehr Dateien, als das Protokoll nennt — und wüsste nicht, ob noch welche unterwegs sind oder
 * ob er zu viele bekommen hat.
 *
 * ⚠️ **Der Anker ist die Datei selbst, nicht die Zeile.** Bis zum 2026-09-14 stand im `value`
 * der Dateipfad, und die Reihenfolge war zwingend. Jetzt ordnet der Server die Anhänge über
 * ihre eigenen Felder zu (`server/anhaenge.ts::felder`); die Reihenfolge bleibt trotzdem, weil
 * sie nichts kostet und das Protokoll so nie hinter den Dateien herhinkt.
 *
 * Der Zeitgeber steht hier und nicht in `sync.ts` oder `anhaenge.ts`: der Lauf selbst bleibt so
 * ohne Uhr prüfbar — dieselbe Bauart wie `classic/takt.ts` und `autosave.ts`.
 *
 * ⚠️ **Er prüft seinen Modus selbst.** Beide Takte werden beim Start angeworfen; wirklich
 * arbeitet immer nur der, zu dem die Einstellung passt (`zugang.ts::alsModus`).
 */

/**
 * Wie Classic: alle zehn Sekunden ein Paket mit allem, was sich angesammelt hat (Vorgabe des
 * Nutzers, 2026-10-02). Bis dahin waren es dreissig Sekunden, und dazu ging jede Ablage sofort
 * hinaus — ohne Netz hiess das ein Fehlversuch je Klick.
 */
export const RELAY_TAKT_MS = 10000;

/**
 * Der Einstellungsabgleich (Befehle holen, Einstellungen melden) läuft nicht in jedem Takt.
 *
 * ⚠️ **30 s, nicht mehr 60 s** (2026-10-10). Die Abfrage ist zugleich das Lebenszeichen für
 * Relays Geräteliste, und Relay zeigt „aktiv“ nur, wenn die letzte höchstens **45 s** her ist.
 * Mit 60 s spränge jedes Gerät zwischen „aktiv“ und „nicht erreichbar“.
 */
export const ABGLEICH_MS = 30000;

/** Die Lebenszeichen-Parameter: Modus, Wechselzeit und was noch hinaus muss. */
export async function lebenszeichen(store: ReturnType<typeof getStore>): Promise<Record<string, string | number | undefined>> {
  const { modus, seit } = appModus();
  const r = await zaehleRueckstand(store).catch(() => null);
  return {
    appMode: modus,
    changedAt: seit ?? undefined,
    outboxPending: r ? r.pakete + r.dateien : undefined,
    appVersion: APP_VERSION_MIT_BUILD,
    osVersion: systemJetzt().version,
  };
}

/**
 * Einmal `SLEEPING` melden — beim Wechsel in den Hintergrund oder beim Sperren. iOS gewährt
 * danach nur wenige Sekunden; kommt die Meldung nicht durch, zeigt Relay das Gerät nach 45 s
 * als „nicht erreichbar“. **Kein Ausgangskorb:** nach dem Aufwachen kommt sofort `ACTIVE`, ein
 * nachgereichtes `SLEEPING` wäre dann falsch. Relay liefert darauf eine leere Liste und
 * markiert nichts als zugestellt.
 */
export async function meldeSchlaf(clientId: string): Promise<void> {
  const store = getStore();
  const zugang = await ladeZugang(store, clientId);
  if (zugang.modus !== 'relay' || !istEingerichtet(zugang)) return;
  const identity = await Signing.getIdentity().catch(() => null);
  const client = baueClient(zugang, {
    deviceIdentifier: identity?.deviceId ?? '',
    deviceName: identity?.deviceName ?? '',
  });
  await client.get('/rest/v1/message/pending', await lebenszeichen(store));
}

/** Ist der Abgleich in diesem Durchgang dran? Eine fällige Meldung oder „Jetzt übertragen" wartet nicht. */
export const abgleichFaellig = (a: { manuell: boolean; meldung: boolean; seitLetztem: number }): boolean =>
  a.manuell || a.meldung || a.seitLetztem >= ABGLEICH_MS;

export interface RelayTaktDeps {
  /**
   * Die Gerätezeile, unter der die Zugangsdaten liegen. Ohne sie liest `ladeZugang` nur die
   * verwalteten Einstellungen des iPads — im Browser also gar nichts.
   */
  clientId: string;
  /** Einschiebbar für Tests; sonst der echte Takt. */
  intervall?: number;
  /** Einschiebbar für Tests; sonst der echte Durchgang. */
  schlagFn?: (manuell: boolean) => Promise<void>;
  /**
   * Wohin der Takt seinen Stand meldet — die Rückstandspille in der Kopfzeile.
   *
   * ⚠️ **Hineingereicht, nicht importiert.** Dieses Modul kennt die Oberfläche nicht und soll
   * sie nicht kennen; angemeldet wird der Melder in `main.tsx` (dieselbe Bauform wie
   * `platform/fotos.ts::setBeschriftungsQuelle`). Fehlt er, läuft der Takt unverändert.
   */
  melder?: Taktmelder;
  /**
   * Bringt Einstellungen, die Relay fernsetzt (`SET_SETTINGS`), in die Oberfläche. Hineingereicht
   * aus demselben Grund wie der Melder; fehlt sie, wirken sie erst beim nächsten Start.
   */
  uebernehmeEinstellungen?: (g: Gemischt) => void;
  /** Was die Gerätebefehle (`WIPE_DATA`, `INSTALL_DEMO`, `TIME_ADD`, `MESSAGE`) von der Oberfläche brauchen. */
  /** Nach der Aufnahme für Relays `SCREENSHOT`: kurz das Symbol zeigen. */
  fotoAufgenommen?: () => void;
  befehle?: Pick<
    AbgleichDeps,
    'bogenOffen' | 'geloescht' | 'demoEingelesen' | 'zeitZugabe' | 'zeigeMitteilung' | 'mitteilungBestaetigt' | 'unbekannteEinstellungen'
  >;
}

export interface Taktmelder {
  /** Ein Durchgang beginnt (`true`) oder ist vorbei (`false`). */
  laeuft: (an: boolean) => void;
  /** Nach dem Durchgang neu zählen, was noch liegt. */
  zaehle: () => void | Promise<void>;
  /** Die letzte Meldung in Prüfersprache — eine Zeile, kein Protokoll. */
  text: (meldung: string) => void;
}

/**
 * Startet den Takt und gibt die Funktion zum Beenden zurück.
 *
 * Ein Durchgang, der scheitert, **beendet den Takt nicht**: der nächste versucht es erneut. Ein
 * Prüfungstag ohne Netz darf nicht dazu führen, dass die Übertragung für den Rest des Tages
 * steht, weil einmal etwas schiefging.
 */
export function starteRelayTakt(deps: RelayTaktDeps): () => void {
  let gestoppt = false;
  /** Wann der Einstellungsabgleich zuletzt durchkam; 0 heisst: gleich beim ersten Durchgang. */
  let letzterAbgleich = 0;

  /**
   * @param manuell „Jetzt übertragen“ — sendet auch bei abgeschaltetem `auto_upload`.
   */
  const durchgang = async (manuell: boolean) => {
    // Ruht `auto_upload`, laufen nur Gerätemeldung und Befehle (Entscheidung des Nutzers,
    // 2026-10-02); Bewertungen und Dateien warten auf „Jetzt übertragen“.
    const senden = manuell || autoUpload();
    const store = getStore();
    const zugang = await ladeZugang(store, deps.clientId);
    if (zugang.modus !== 'relay' || !istEingerichtet(zugang)) return;

    const identity = await Signing.getIdentity().catch(() => null);
    const client = baueClient(zugang, {
      deviceIdentifier: identity?.deviceId ?? '',
      deviceName: identity?.deviceName ?? '',
    });

    // ⚠️ Über `createSync`, nicht geradewegs: der Merker darin ist modulweit und hält den Takt
    // und ein gleichzeitiges „Jetzt übertragen" aus den Einstellungen auseinander. Sonst läse
    // jeder von beiden denselben Rückstand und schickte jede Zeile doppelt.
    const ergebnis = senden
      ? await createSync({ store, client, log: appendAppLog }).jetztUebertragen()
      : { uebertragen: 0, liegengeblieben: 0, netzausfall: false };
    /*
     * Eine Zeile für den Aufklapper der Pille — in der Sprache des Prüfers, nicht in der des
     * Protokolls. Was genau geschah, steht ohnehin im Dateilog.
     */
    // Kein Netz: Abgleich und Dateien scheiterten am selben Grund. Der nächste Takt versucht es.
    if (ergebnis.netzausfall) return;
    if (ergebnis.uebertragen > 0 || ergebnis.liegengeblieben > 0) {
      deps.melder?.text(
        String(ergebnis.uebertragen) + ' übertragen' +
          (ergebnis.liegengeblieben > 0
            ? ', ' + String(ergebnis.liegengeblieben) + ' liegen noch'
            : ''),
      );
    }

    /*
     * Die Geräteeinstellungen (`geraeteEinstellungen.ts`): Befehle holen, anwenden, quittieren,
     * melden. **Vor** den Dateien — die sind gross und langsam, ein Befehl der Verwaltung soll
     * nicht hinter einem Video warten. Und **eigenständig gefangen**: ein Fehler hier darf die
     * Dateien dieses Durchgangs nicht aufhalten.
     */
    if (abgleichFaellig({ manuell, meldung: meldungFaellig(), seitLetztem: Date.now() - letzterAbgleich })) {
      try {
        await gleicheEinstellungenAb({
          store,
          clientId: deps.clientId,
          client,
          log: appendAppLog,
          uebernehmen: deps.uebernehmeEinstellungen,
          zustand: () => lebenszeichen(store),
          protokolleHochladen: (uploadID, attachments) =>
            ladeProtokolleHoch({ client, basisUrl: zugang.url, store, uploadID, attachments }),
          bildschirmfotoHochladen: (screenshotID) =>
            ladeBildschirmfotoHoch({ client, basisUrl: zugang.url, screenshotID, aufgenommen: deps.fotoAufgenommen }),
          ...deps.befehle,
        });
        letzterAbgleich = Date.now();
      } catch (fehler) {
        if (istNetzausfall(fehler)) return;
        await appendAppLog('Relay: Einstellungsabgleich gescheitert: ' + String(fehler), 'warn');
      }
    }

    /*
     * Das Token holt die Anmeldung. Sie gibt ein vorhandenes zurück, statt ein neues zu
     * erzwingen — Relay zählt jede Anmeldung auf seine Ratenbegrenzung (`client.ts`), und ein
     * Takt alle zehn Sekunden wäre dort sonst der beste Weg, sich selbst auszusperren.
     */
    if (!senden) return;
    const anmeldung = await client.anmelden();
    await uebertrageAnhaenge({
      store,
      basisUrl: zugang.url,
      token: anmeldung.token,
      senden: relaySender(anmeldung.token),
      log: appendAppLog,
    });
  };

  /**
   * ⚠️ **Ein Durchgang darf nicht in den nächsten hineinlaufen.** Ein Video von dreissig
   * Megabyte braucht in einer belegten Prüfungs-WLAN-Zelle länger als zehn Sekunden. Der
   * Übertragungsstand einer Datei entsteht aber erst **nach** dem Versuch — ein zweiter
   * Durchgang sähe sie deshalb als offen und schickte dieselben Bytes ein zweites Mal.
   * `wiederholeBisRuhe` hält sie auseinander und merkt sich einen Anstoss währenddessen, statt
   * ihn zu verwerfen.
   */
  const einmal = wiederholeBisRuhe(async (manuell: boolean) => {
    if (gestoppt) return;
    deps.melder?.laeuft(true);
    try {
      await (deps.schlagFn ?? durchgang)(manuell);
    } catch (fehler) {
      // Geschluckt und protokolliert, aus dem Grund oben. Kein Netz steht schon einmal je
      // Offline-Phase im Log (`verbindung.ts`).
      if (!istNetzausfall(fehler)) {
        await appendAppLog('Relay-Takt: ' + String(fehler), 'error').catch(() => undefined);
      }
      deps.melder?.text(String(fehler));
    } finally {
      deps.melder?.laeuft(false);
      // Erst jetzt zählen: der Stand entsteht im Durchgang, nicht davor.
      void deps.melder?.zaehle();
    }
  });
  const schlag = (manuell = false) => einmal(manuell);

  // Ohne `window.`: dieses Modul ist kein Bestandteil der Oberfläche und soll auch in einer
  // Umgebung ohne DOM laufen.
  const id = setInterval(() => void schlag(), deps.intervall ?? RELAY_TAKT_MS);
  // Eine geänderte Einstellung weckt den Takt, statt bis zu zehn Sekunden zu warten.
  const abmelden = beiEinstellungsAenderung(markiereMeldungFaellig);
  setzeMeldeWecker(() => void schlag());
  // Sofort senden, sobald etwas abgelegt ist (`platform/anstoss.ts`).
  const abmeldenWecker = meldeWecker(schlag);
  // Nicht erst nach dem ersten Intervall: was schon liegt, soll gleich hinaus.
  void schlag();

  return () => {
    gestoppt = true;
    clearInterval(id);
    abmelden();
    abmeldenWecker();
    setzeMeldeWecker(null);
  };
}
